import React from 'react';
import { MessageCircle, Clock, Zap } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const ContactSection = () => {
  const [titleRef, titleInView] = useInView();
  const [cardsRef, cardsInView] = useInView();
  const [guaranteeRef, guaranteeInView] = useInView({ threshold: 0.3 });
  const { language } = useLanguage();

  const handleWhatsAppClick = () => {
    const phoneNumber = "+21650878032";
    const message = language === 'fr' 
      ? "Bonjour Wassim, je souhaite discuter d'un projet UI/UX avec vous."
      : "Hello Wassim, I would like to discuss a UI/UX project with you.";
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\+/g, '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const contactOptions = [
    {
      titleKey: "contact.option1.title",
      descriptionKey: "contact.option1.description",
      buttonTextKey: "contact.option1.button",
      buttonAction: () => window.open('https://comeup.com/fr/@kayti-creation', '_blank'),
      buttonStyle: "bg-[#FFD700] hover:bg-[#FFC107] text-black font-medium shadow-lg hover:shadow-xl",
      highlight: true
    },
    {
      titleKey: "contact.option2.title",
      descriptionKey: "contact.option2.description",
      buttonTextKey: "contact.option2.button",
      buttonAction: handleWhatsAppClick,
      buttonStyle: "bg-[#25D366] hover:bg-[#20B858] text-white font-medium shadow-lg hover:shadow-xl",
      buttonIcon: <MessageCircle className="w-5 h-5 mr-2" />,
      highlight: false
    }
  ];

  return (
    <section id="contact" className="pt-2 md:pt-[6.24rem] pb-32 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1200 ease-out ${
            titleInView 
              ? 'opacity-100 translate-y-0 scale-100 rotate-0' 
              : 'opacity-0 translate-y-20 scale-90 rotate-1'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            <span className="gradient-text">{getTranslation(language, 'contact.title')}</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {getTranslation(language, 'contact.subtitle')}
          </p>
        </div>

        {/* Contact Options - Aligned Text */}
        <div 
          ref={cardsRef}
          className="grid lg:grid-cols-2 gap-8 mb-16 items-stretch"
        >
          {contactOptions.map((option, index) => (
            <Card 
              key={index}
              className={`relative overflow-hidden p-8 group flex flex-col justify-between min-h-[320px] transition-all duration-1000 hover:scale-110 hover:-translate-y-3 hover:shadow-2xl ${
                option.highlight 
                  ? 'bg-gradient-to-br from-gray-900 to-gray-800 border-gray-600' 
                  : 'bg-gray-900/50 border-gray-800'
              }`}
              style={{
                transform: cardsInView 
                  ? 'translateX(0) translateY(0) rotateY(0deg) scale(1)' 
                  : `translateX(${index % 2 === 0 ? '-100' : '100'}px) translateY(50px) rotateY(${index % 2 === 0 ? '-' : ''}20deg) scale(0.8)`,
                opacity: cardsInView ? 1 : 0,
                filter: cardsInView ? 'blur(0px)' : 'blur(8px)',
                transitionDelay: cardsInView ? `${index * 200}ms` : '0ms',
                transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                transformStyle: 'preserve-3d',
                perspective: '1000px'
              }}
            >
              <div className="text-center space-y-6 flex-1 flex flex-col justify-center">
                {/* Title */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {getTranslation(language, 'contact.optionLabel')} {index + 1} : {getTranslation(language, option.titleKey)}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-center">
                  {getTranslation(language, option.descriptionKey)}
                </p>
              </div>

              {/* CTA Button */}
              <div className="mt-6">
                <Button
                  onClick={option.buttonAction}
                  className={`w-full py-3 rounded-lg transition-all duration-300 flex items-center justify-center ${option.buttonStyle}`}
                >
                  {option.buttonIcon}
                  {getTranslation(language, option.buttonTextKey)}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Reactivity Guarantee - Uniform Structure */}
        <div 
          ref={guaranteeRef}
          className="text-center"
        >
          <Card 
            className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 border border-gray-700 p-8 max-w-4xl mx-auto transition-all duration-1000"
            style={{
              transform: guaranteeInView 
                ? 'translateY(0) scale(1)' 
                : 'translateY(60px) scale(0.95)',
              opacity: guaranteeInView ? 1 : 0,
              filter: guaranteeInView ? 'blur(0px)' : 'blur(8px)',
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            <h3 
              className="text-2xl font-bold text-white mb-8 transition-all duration-700"
              style={{
                transform: guaranteeInView ? 'translateY(0)' : 'translateY(20px)',
                opacity: guaranteeInView ? 1 : 0,
                transitionDelay: guaranteeInView ? '200ms' : '0ms'
              }}
            >
              {getTranslation(language, 'contact.guarantee.title')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div 
                className="text-center group hover:scale-110 transition-all duration-500"
                style={{
                  transform: guaranteeInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.9)',
                  opacity: guaranteeInView ? 1 : 0,
                  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: guaranteeInView ? '300ms' : '0ms'
                }}
              >
                <div className="p-3 bg-gray-800 rounded-xl inline-flex mb-4 group-hover:bg-[#DC1A5F]/20 transition-colors">
                  <Zap className="w-6 h-6 icon-primary" />
                </div>
                <div className="text-lg font-semibold text-white mb-2">{getTranslation(language, 'contact.guarantee.item1.title')}</div>
                <div className="text-gray-400 text-sm">{getTranslation(language, 'contact.guarantee.item1.subtitle')}</div>
              </div>
              
              <div 
                className="text-center group hover:scale-110 transition-all duration-500"
                style={{
                  transform: guaranteeInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.9)',
                  opacity: guaranteeInView ? 1 : 0,
                  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: guaranteeInView ? '450ms' : '0ms'
                }}
              >
                <div className="p-3 bg-gray-800 rounded-xl inline-flex mb-4 group-hover:bg-[#32D6E0]/20 transition-colors">
                  <Clock className="w-6 h-6 icon-secondary" />
                </div>
                <div className="text-lg font-semibold text-white mb-2">{getTranslation(language, 'contact.guarantee.item2.title')}</div>
                <div className="text-gray-400 text-sm">{getTranslation(language, 'contact.guarantee.item2.subtitle')}</div>
              </div>
              
              <div 
                className="text-center group hover:scale-110 transition-all duration-500"
                style={{
                  transform: guaranteeInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.9)',
                  opacity: guaranteeInView ? 1 : 0,
                  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: guaranteeInView ? '600ms' : '0ms'
                }}
              >
                <div className="p-3 bg-gray-800 rounded-xl inline-flex mb-4 group-hover:bg-[#DC1A5F]/20 transition-colors">
                  <Clock className="w-6 h-6 icon-primary" />
                </div>
                <div className="text-lg font-semibold text-white mb-2">{getTranslation(language, 'contact.guarantee.item3.title')}</div>
                <div className="text-gray-400 text-sm">{getTranslation(language, 'contact.guarantee.item3.subtitle')}</div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </section>
  );
};

export default ContactSection;